import * as flatbuffers from "flatbuffers";
import { tracePointEmit, TracePointId, TraceOutcome } from "../../integrationTrace";

// ROOT
import {
  FrontEndMessage,
  FrontEndMessageT,
  verifyRootAsFrontEndMessage,
} from "../generated/front-end/front-end-message";
import {
  BatchedFrontEndMessage,
  BatchedFrontEndMessageT,
  verifyRootAsBatchedFrontEndMessage,
} from "../generated/batched-front-end/batched-front-end-message";

// LN2 message chain (T types)
import { EncodedMessageContent } from "../generated/ln2/encoded-message-content";
import { MessageContent } from "../generated/ln2/message-content";
import { MessageT } from "../generated/ln2/message";
import { BitMaskMessageT } from "../generated/ln2/bit-mask-message";
import { OneHotMessageT } from "../generated/ln2/one-hot-message";

// TRAFFIC
import { DeviceTrafficMessage } from "../generated/traffic/device-traffic-message";
import { ElevatorTrafficMessageT } from "../generated/traffic/elevator-traffic-message";
import { EscalatorTrafficMessageT } from "../generated/traffic/escalator-traffic-message";
import { TrafficMessageT } from "../generated/traffic/traffic-message";

// CONTROL
import { DeviceControlMessage } from "../generated/control/device-control-message";
import { ElevatorControlMessageT } from "../generated/control/elevator-control-message";
import { EscalatorControlMessageT } from "../generated/control/escalator-control-message";
import { ControlMessageT } from "../generated/control/control-message";

// USAGE
import { DeviceUsageContent } from "../generated/usage/device-usage-content";
import { ElevatorUsageMessageT } from "../generated/usage/elevator-usage-message";
import { EscalatorUsageMessageT } from "../generated/usage/escalator-usage-message";
import { UsageMessageT } from "../generated/usage/usage-message";

// FAULT
import { FaultContent } from "../generated/fault/fault-content";
import { FaultMessageT } from "../generated/fault/fault-message";

// SERVICE
import { DeviceServiceContent } from "../generated/service/device-service-content";
import { ServiceMessageT } from "../generated/service/service-message";

// STATUS
import { DeviceStatusContent } from "../generated/status/device-status-content";
import { StatusMessageT } from "../generated/status/status-message";
import { ElevatorStatusMessageT } from "../generated/status/elevator-status-message";
import { EscalatorStatusMessageT } from "../generated/status/escalator-status-message";
import { Status } from "../generated/status/status";

// DEBOUNCE KEY
import {
  debounceKey,
  DEVICE_ELEVATOR,
  DEVICE_ESCALATOR,
  DEVICE_STATUS,
} from "../../utils/debounceLookup";

// DEV LOGGING
import { devLog, devWarn } from "../../utils/devLog";

// ---------------------------------------------------------
// Helper: extract the inner enum code from a variant union
// ---------------------------------------------------------
function variantMessage(variant: any): number {
  if (variant && typeof variant.message === "number") return variant.message;
  return 0;
}

/**
 * Maps a server-side `message_type_id` to its `MessageContent` enum value. The API ids and the
 * FlatBuffer enum share values, so the id is returned unchanged.
 */
export function messageContentFromTypeId(id: number): number {
  return id;
}

// ---------------------------------------------------------
// Decode statistics — useful for diagnostics
// ---------------------------------------------------------
let decodeTotal = 0;
let decodeFailures = 0;

/**
 * Returns how many frames decodeFrontEndMessage has been given and how many of them it rejected.
 */
export function getDecodeStats(): { failures: number; total: number } {
  return { failures: decodeFailures, total: decodeTotal };
}

/**
 * Zeroes the decodeFrontEndMessage counters.
 */
export function resetDecodeStats(): void {
  decodeTotal = 0;
  decodeFailures = 0;
}

function bytesToHex(bytes: Uint8Array, maxBytes: number): string {
  return Array.from(bytes.slice(0, maxBytes))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join(" ");
}

// ---------------------------------------------------------
// Robust root verifier (defense-in-depth).
//
// The generated FlatBuffer `verifyRootAs*` helpers structurally validate a
// buffer (bounds, vtable consistency, required fields, nesting limits) BEFORE
// any field is read — guarding against truncated, corrupt, or malicious frames.
//
// The decoders import `verifyRootAsFrontEndMessage` and
// `verifyRootAsBatchedFrontEndMessage` statically, so a generation without them
// fails `npm run check:types` and the webpack build (CRA sets
// strictExportPresence) instead of shipping. Calling an undefined verifier
// directly would throw and null EVERY message (a regression hit once when a
// stale schema checkout shipped without verifiers), so this wrapper still
// treats a missing verifier as "unavailable" — for callers that pass a
// non-function, and under Jest, where Babel does not check named exports:
//   - verifier present + passes -> "ok"          (buffer validated)
//   - verifier present + throws -> "invalid"     (reject the buffer)
//   - verifier absent           -> "unavailable" (fall through to the unpack
//                                                 gate; never fail the decode)
// The "unavailable" case emits a one-time dev warning, and the unit test that
// runs the imported verifier ("uses the generated FrontEndMessage verifier ...")
// fails if the shipped generation lacks it.
// ---------------------------------------------------------
const verifierUnavailableWarned = new Set<string>();

/**
 * True for the plain `{ kind, field?, offset, trace }` object the flatbuffers verifier throws in
 * place of an Error.
 */
function isVerificationError(e: unknown): e is flatbuffers.VerificationError {
  if (typeof e !== "object" || e === null) return false;
  const { kind, offset } = e as Partial<flatbuffers.VerificationError>;
  return Object.values<unknown>(flatbuffers.ErrorKind).includes(kind) && typeof offset === "number";
}

/**
 * Renders what a verifier threw as readable text: an Error's message, or "<kind> on <field> at
 * offset <n>" for a verifier error object, which `String()` would print as "[object Object]".
 */
function describeVerifierError(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (isVerificationError(e)) {
    return `${e.kind}${e.field ? ` on ${e.field}` : ""} at offset ${e.offset}`;
  }
  return String(e);
}

/**
 * Runs a generated `verifyRootAs*` verifier over `bytes`: "ok" when it passes, "invalid" when it
 * throws, "unavailable" when `verifyFn` is not a function (see the comment above).
 */
export function runRootVerifier(
  verifyFn: unknown,
  bytes: Uint8Array,
  label: string,
): "ok" | "invalid" | "unavailable" {
  if (typeof verifyFn !== "function") {
    if (process.env.NODE_ENV === "development" && !verifierUnavailableWarned.has(label)) {
      verifierUnavailableWarned.add(label);
      devWarn(
        `[FlatBuffer] verifyRootAs${label} is unavailable in the generated module — ` +
          "structural verification is disabled, relying on the unpack gate. " +
          "Check the flatbuffer_schemas generation.",
      );
    }
    return "unavailable";
  }

  try {
    (verifyFn as (view: DataView) => void)(
      new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength),
    );
    return "ok";
  } catch (e) {
    const errorMessage = describeVerifierError(e);
    tracePointEmit(TracePointId.FrontendDecodeError, undefined, {
      outcome: TraceOutcome.Error,
      errorMessage,
    });
    if (process.env.NODE_ENV === "development") {
      devWarn(
        `[FlatBuffer] Verification failed for ${label}: ${errorMessage}. Bytes[0:32]: ${bytesToHex(bytes, 32)}`,
      );
    }
    return "invalid";
  }
}

// ---------------------------------------------------------
// Binary dedup: cache last-seen raw bytes per dedup key (bank, lift,
// content type, active)
// ---------------------------------------------------------
const lastSeenBuffers: Map<string, Uint8Array> = new Map();

let dedupeStats = { total: 0, deduped: 0 };

/**
 * Returns how many batched messages were seen, how many were skipped as byte-identical repeats,
 * and the skipped share.
 */
export function getDedupeStats(): { total: number; deduped: number; ratio: number } {
  const { total, deduped } = dedupeStats;
  return { total, deduped, ratio: total === 0 ? 0 : deduped / total };
}

/**
 * Zeroes the dedup counters and forgets every cached message, so the next message for each
 * dedup key (bank, lift, content type, active) is unpacked.
 */
export function resetDedupeStats(): void {
  dedupeStats = { total: 0, deduped: 0 };
  lastSeenBuffers.clear();
}

/**
 * Build a string dedup key from the bankId, liftId, message content type,
 * and active flag — all read without calling unpack().
 * Including the content type discriminant and active flag ensures that
 * different message types on the same lift (e.g. Position vs Service) land
 * in separate dedup buckets, preventing OneHot messages whose inline bytes
 * share the same vtable layout from being incorrectly collapsed.
 * Returns null if either UUID is absent (message must not be deduped).
 */
function buildDedupeKey(fem: FrontEndMessage): string | null {
  const bankId = fem.bankId();
  const liftId = fem.liftId();
  if (!bankId || !liftId) return null;
  const bankVal = bankId.value();
  const liftVal = liftId.value();
  if (!bankVal || !liftVal) return null;
  const valuedMsg = fem.msg();
  const msgType = valuedMsg?.msg()?.contentType() ?? -1;
  const active = valuedMsg?.active() ?? false;
  return `${bankVal.upper()}_${bankVal.lower()}_${liftVal.upper()}_${liftVal.lower()}_${msgType}_${
    active ? 1 : 0
  }`;
}

/**
 * Extract the inline table bytes for a FrontEndMessage embedded in the batch
 * ByteBuffer. Uses the vtable to find the inline object size.
 * The inline data includes the vtable pointer and all inline field slots
 * (which are relative offsets to nested sub-tables). When the server packs
 * deterministically, identical content produces identical inline bytes.
 */
function extractFemInlineBytes(fem: FrontEndMessage, bb: flatbuffers.ByteBuffer): Uint8Array {
  const vtable = fem.bb_pos - bb.readInt32(fem.bb_pos);
  const objectSize = bb.readInt16(vtable + 2);
  return bb.bytes().slice(fem.bb_pos, fem.bb_pos + objectSize);
}

/**
 * Byte-for-byte comparison of two Uint8Arrays.
 */
function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * Decodes one FrontEndMessage frame into its Object API form. Returns null for a frame that is too
 * short, fails verification, lacks a bank id, lift id or payload, or throws while unpacking.
 */
export function decodeFrontEndMessage(bytes: Uint8Array): FrontEndMessageT | null {
  decodeTotal += 1;

  if (bytes.length < 4) {
    decodeFailures += 1;
    if (process.env.NODE_ENV === "development") {
      devWarn(`[FlatBuffer] Message too short: ${bytes.length} bytes`);
    }
    return null;
  }

  try {
    // Defense-in-depth: structurally verify the buffer before unpacking. The verifier is
    // imported statically, so a generation without it fails the type-check. A present
    // verifier that throws means the buffer is malformed and must be rejected.
    if (runRootVerifier(verifyRootAsFrontEndMessage, bytes, "FrontEndMessage") === "invalid") {
      decodeFailures += 1;
      return null;
    }

    const bb = new flatbuffers.ByteBuffer(bytes);
    const fem = FrontEndMessage.getRootAsFrontEndMessage(bb).unpack();

    if (process.env.NODE_ENV === "development") {
      const encoded = fem.msg?.msg;
      const contentLabel = encoded
        ? (EncodedMessageContent[encoded.contentType] ?? encoded.contentType)
        : "none";
      const msgTypeLabel = encoded
        ? (() => {
            if (encoded.contentType === EncodedMessageContent.BitMaskMessage) {
              const bm = encoded.content as BitMaskMessageT;
              return MessageContent[bm?.messageType] ?? bm?.messageType;
            }
            if (encoded.contentType === EncodedMessageContent.OneHotMessage) {
              const oh = encoded.content as OneHotMessageT;
              return MessageContent[oh?.messageType] ?? oh?.messageType;
            }
            return "?";
          })()
        : "none";
      devLog(
        `[DECODE] FEM: bankId=${!!fem.bankId} liftId=${!!fem.liftId} active=${
          fem.msg?.active
        } encoding=${contentLabel} msgType=${msgTypeLabel}`,
        fem.msg?.msg?.content,
      );
    }

    if (!fem.bankId || !fem.liftId) {
      decodeFailures += 1;
      if (process.env.NODE_ENV === "development") {
        const hex = bytesToHex(bytes, 32);
        const missing = [!fem.bankId && "bankId", !fem.liftId && "liftId"].filter(Boolean);
        devWarn(
          `[FlatBuffer] Missing identity fields (${missing.join(", ")}). Bytes[0:32]: ${hex}`,
        );
      }
      return null;
    }

    if (!fem.msg?.msg) {
      decodeFailures += 1;
      if (process.env.NODE_ENV === "development") {
        devWarn(
          `[FlatBuffer] Message has identity but no payload (msg=${!!fem.msg}, msg.msg=${!!fem.msg
            ?.msg})`,
        );
      }
      return null;
    }

    tracePointEmit(TracePointId.FrontendFemDecoded);
    return fem;
  } catch (e) {
    decodeFailures += 1;
    tracePointEmit(TracePointId.FrontendDecodeError, undefined, {
      outcome: TraceOutcome.Error,
      errorMessage: e instanceof Error ? e.message : String(e),
    });
    if (process.env.NODE_ENV === "development") {
      const hex = bytesToHex(bytes, 32);
      devWarn(`[FlatBuffer] Decode error: ${e}. Bytes[0:32]: ${hex}`);
    }
    return null;
  }
}

/**
 * Decodes a size-prefixed BatchedFrontEndMessage, which the cloud sends at 5 Hz. Messages whose
 * inline bytes match the last-seen bytes for their dedup key (bank, lift, content type, active)
 * are skipped without unpacking.
 * Returns null for a buffer too short to hold a frame, one that fails verification, or one that
 * throws while decoding.
 * `skip` names messages (indices into the batch) not to read at all: those the schema gate found
 * holding a value this bundle has no name for.
 */
export function decodeBatchedFrontEndMessage(
  bytes: Uint8Array,
  skip?: ReadonlySet<number>,
): BatchedFrontEndMessageT | null {
  if (bytes.length < flatbuffers.SIZE_PREFIX_LENGTH + 4) return null;

  try {
    // Strip the 4-byte size prefix before verification — the server sends
    // length-prefixed FlatBuffers; verifyRootAs* reads position 0 as the root
    // offset, so the prefix must be removed first.
    const payload = bytes.subarray(flatbuffers.SIZE_PREFIX_LENGTH);

    // Defense-in-depth: structurally verify before unpacking. The verifier is
    // imported statically, so a generation without it fails the type-check.
    if (
      runRootVerifier(verifyRootAsBatchedFrontEndMessage, payload, "BatchedFrontEndMessage") ===
      "invalid"
    ) {
      return null;
    }

    const bb = new flatbuffers.ByteBuffer(bytes);
    const batch = BatchedFrontEndMessage.getSizePrefixedRootAsBatchedFrontEndMessage(bb);
    const count = batch.messagesLength();
    const filteredMessages: FrontEndMessageT[] = [];

    for (let i = 0; i < count; i += 1) {
      const fem = skip?.has(i) ? null : batch.messages(i);
      if (fem) {
        dedupeStats.total += 1;

        const key = buildDedupeKey(fem);
        let duplicate = false;
        if (key !== null) {
          const inlineBytes = extractFemInlineBytes(fem, bb);
          const cached = lastSeenBuffers.get(key);
          duplicate = cached !== undefined && bytesEqual(cached, inlineBytes);
          if (duplicate) {
            dedupeStats.deduped += 1;
          } else {
            lastSeenBuffers.set(key, inlineBytes);
          }
        }

        if (!duplicate) {
          const unpacked = fem.unpack();
          filteredMessages.push(unpacked);

          if (process.env.NODE_ENV === "development") {
            const bankVal = fem.bankId()?.value();
            const liftVal = fem.liftId()?.value();
            const bankDisplay = bankVal ? bankVal.upper().toString(16).slice(0, 8) : "unknown";
            const liftDisplay = liftVal ? liftVal.upper().toString(16).slice(0, 8) : "unknown";
            const builder = new flatbuffers.Builder(512);
            const offset = unpacked.pack(builder);
            FrontEndMessage.finishFrontEndMessageBuffer(builder, offset);
            const standaloneBytes = builder.asUint8Array();
            // Fire-and-forget — errors must not affect production message flow
            import("../tools/liveMessageDiff")
              .then(({ recordMessage }) =>
                recordMessage(
                  key ?? `no-key-${i}`,
                  `bank:${bankDisplay}`,
                  `lift:${liftDisplay}`,
                  standaloneBytes,
                ),
              )
              .catch((err) => devWarn("[liveMessageDiff] recording failed", err));
          }
        }
      }
    }

    tracePointEmit(TracePointId.FrontendBatchDecoded);
    return new BatchedFrontEndMessageT(batch.timestamp(), filteredMessages);
  } catch (e) {
    tracePointEmit(TracePointId.FrontendDecodeError, undefined, {
      outcome: TraceOutcome.Error,
      errorMessage: e instanceof Error ? e.message : String(e),
    });
    if (process.env.NODE_ENV === "development") {
      const hex = bytesToHex(bytes, 32);
      devWarn(
        `[FlatBuffer] Exception in decodeBatchedFrontEndMessage: ${
          e instanceof Error ? e.message : String(e)
        }. Bytes[0:32]: ${hex}`,
      );
    }
    return null;
  }
}

/**
 * Returns the elevator or escalator status carried by a StatusMessageT when it is Connected,
 * Disconnected or Heartbeat, and null otherwise. Replaces the old ConnectionStatusMessage
 * extraction.
 */
export function extractConnectionStatus(
  statusMsg: StatusMessageT,
): ElevatorStatusMessageT | EscalatorStatusMessageT | null {
  switch (statusMsg.statusType) {
    case DeviceStatusContent.ElevatorStatusMessage: {
      const elev = statusMsg.status as ElevatorStatusMessageT | null;
      if (elev) {
        const code = variantMessage(elev.status);
        // Check if this is a connection-related status (Connected/Disconnected/Heartbeat)
        if (
          code === Status.Connected ||
          code === Status.Disconnected ||
          code === Status.Heartbeat
        ) {
          return elev;
        }
      }
      break;
    }
    case DeviceStatusContent.EscalatorStatusMessage: {
      const esc = statusMsg.status as EscalatorStatusMessageT | null;
      if (esc) {
        const code = variantMessage(esc.status);
        if (
          code === Status.Connected ||
          code === Status.Disconnected ||
          code === Status.Heartbeat
        ) {
          return esc;
        }
      }
      break;
    }
    default:
      break;
  }
  return null;
}

/**
 * Reports whether a FrontEndMessageT carries a bitmask-encoded fault message.
 */
export function isFaultMessage(fem: FrontEndMessageT): boolean {
  const encoded = fem.msg?.msg;
  if (!encoded || encoded.contentType !== EncodedMessageContent.BitMaskMessage) return false;
  return (encoded.content as BitMaskMessageT).messageType === MessageContent.Fault_FaultMessage;
}

/**
 * Computes a numeric composite debounce key from the FlatBuffer union hierarchy: a packed integer
 * encoding (deviceType, MessageContent, enumCode) — see debounceKey() — or undefined for an
 * unrecognised message.
 */
export function computeDebounceKey(fem: FrontEndMessageT): number | undefined {
  return debounceKeyForEncoded(fem.msg?.msg);
}

/**
 * The same identity, computed from the encoded union alone.
 *
 * `computeDebounceKey` only ever read `fem.msg?.msg`, so this is that body with
 * the envelope peeled off — behaviour is unchanged and the public entry point
 * is now a one-line wrapper.
 *
 * It exists because a consumer holding a decoded socket message has the union
 * but not the `FrontEndMessageT` around it. Action verification is the case:
 * it must decide "is this the message the operator's action said to expect?",
 * and that question is exactly "do these two messages have the same identity" —
 * already answered here, for every category, through the full union hierarchy.
 * Re-deriving it beside this function would be a second definition of message
 * identity that could drift from the one debouncing uses.
 */
export function debounceKeyForEncoded(encoded: MessageT | null | undefined): number | undefined {
  if (!encoded) return undefined;

  if (encoded.contentType === EncodedMessageContent.BitMaskMessage) {
    const bm = encoded.content as BitMaskMessageT;
    switch (bm.messageType) {
      case MessageContent.Traffic_TrafficMessage: {
        const traffic = bm.message as TrafficMessageT;
        switch (traffic.messageType) {
          case DeviceTrafficMessage.ElevatorTrafficMessage:
            return debounceKey(
              DEVICE_ELEVATOR,
              MessageContent.Traffic_TrafficMessage,
              variantMessage((traffic.message as ElevatorTrafficMessageT).traffic),
            );
          case DeviceTrafficMessage.EscalatorTrafficMessage:
            return debounceKey(
              DEVICE_ESCALATOR,
              MessageContent.Traffic_TrafficMessage,
              variantMessage((traffic.message as EscalatorTrafficMessageT).traffic),
            );
          default:
            return undefined;
        }
      }
      case MessageContent.Control_ControlMessage: {
        const control = bm.message as ControlMessageT;
        switch (control.controlType) {
          case DeviceControlMessage.ElevatorControlMessage:
            return debounceKey(
              DEVICE_ELEVATOR,
              MessageContent.Control_ControlMessage,
              variantMessage((control.control as ElevatorControlMessageT).control),
            );
          case DeviceControlMessage.EscalatorControlMessage:
            return debounceKey(
              DEVICE_ESCALATOR,
              MessageContent.Control_ControlMessage,
              variantMessage((control.control as EscalatorControlMessageT).control),
            );
          default:
            return undefined;
        }
      }
      case MessageContent.Fault_FaultMessage: {
        const fault = bm.message as FaultMessageT;
        const devType =
          fault.faultType === FaultContent.ElevatorFaultMessage
            ? DEVICE_ELEVATOR
            : DEVICE_ESCALATOR;
        return debounceKey(
          devType,
          MessageContent.Fault_FaultMessage,
          variantMessage(fault.fault?.fault),
        );
      }
      case MessageContent.Status_StatusMessage: {
        const conn = extractConnectionStatus(bm.message as StatusMessageT);
        if (conn) {
          return debounceKey(
            DEVICE_STATUS,
            MessageContent.Status_StatusMessage,
            variantMessage(conn.status),
          );
        }
        return undefined;
      }
      default:
        return undefined;
    }
  }

  if (encoded.contentType === EncodedMessageContent.OneHotMessage) {
    const oh = encoded.content as OneHotMessageT;
    switch (oh.messageType) {
      case MessageContent.Usage_UsageMessage: {
        const usage = oh.message as UsageMessageT;
        switch (usage.messageType) {
          case DeviceUsageContent.ElevatorUsageMessage:
            return debounceKey(
              DEVICE_ELEVATOR,
              MessageContent.Usage_UsageMessage,
              variantMessage((usage.message as ElevatorUsageMessageT).usage),
            );
          case DeviceUsageContent.EscalatorUsageMessage:
            return debounceKey(
              DEVICE_ESCALATOR,
              MessageContent.Usage_UsageMessage,
              variantMessage((usage.message as EscalatorUsageMessageT).usage),
            );
          default:
            return undefined;
        }
      }
      case MessageContent.Service_ServiceMessage: {
        const service = oh.message as ServiceMessageT;
        const devType =
          service.messageType === DeviceServiceContent.ElevatorServiceMessage
            ? DEVICE_ELEVATOR
            : DEVICE_ESCALATOR;
        return debounceKey(
          devType,
          MessageContent.Service_ServiceMessage,
          variantMessage(service.message?.message),
        );
      }
      default:
        return undefined;
    }
  }

  return undefined;
}
